import { NotificationType } from '@bannerflow/types.notification-service';
import { ImageExportNotification, VideoExportNotification } from '../domain';

export function getVideoExportTitle(
  notification: VideoExportNotification
): string {
  switch (notification.type) {
    case NotificationType.Error:
      return 'Export failed';
    case NotificationType.Success:
      return 'Export finished';
    case NotificationType.Information:
      return 'Export started';
    default:
      return 'VideoExport';
  }
}

export function getImageExportTitle(
  notification: ImageExportNotification
): string {
  switch (notification.type) {
    case NotificationType.Error:
      return 'Export failed';
    case NotificationType.Success:
      return 'Export finished';
    case NotificationType.Information:
      return 'Export started';
    default:
      return 'ImageExport';
  }
}

function describeCount(
  count: string | undefined,
  singular: string,
  plural: string,
  outcome: string
): string | undefined {
  // "0 images failed to export." describes nothing, and a failure can arrive without its count.
  if (!count || count === '0') {
    return undefined;
  }
  return `${count} ${count === '1' ? singular : plural} ${outcome}.`;
}

// Absent, zero, one or malformed all mean a single-set export, which renders as it always has.
function isMultiSetExport(numberOfCreativeSets: string | undefined): boolean {
  return Number(numberOfCreativeSets) > 1;
}

function getExportDescription(
  notification: ImageExportNotification | VideoExportNotification,
  singular: string,
  plural: string
): string | undefined {
  const {
    numberOfConversions,
    numberOfSuccessfulConversions,
    numberOfCreativeSets
  } = notification.content.metadata;

  if (!isMultiSetExport(numberOfCreativeSets)) {
    return undefined;
  }

  switch (notification.type) {
    case NotificationType.Success:
      return describeCount(
        numberOfSuccessfulConversions,
        singular,
        plural,
        'successfully exported'
      );
    case NotificationType.Error:
      return describeCount(
        numberOfConversions,
        singular,
        plural,
        'failed to export'
      );
    default:
      return undefined;
  }
}

export function getVideoExportDescription(
  notification: VideoExportNotification
): string | undefined {
  return getExportDescription(notification, 'video', 'videos');
}

export function getImageExportDescription(
  notification: ImageExportNotification
): string | undefined {
  return getExportDescription(notification, 'image', 'images');
}

export function getExportSourceName(
  notification: ImageExportNotification | VideoExportNotification
): string {
  const { exportName, creativeSetName, numberOfCreativeSets } =
    notification.content.metadata;
  if (exportName) {
    return exportName;
  }
  // Never let one set's name stand in for a batch.
  if (isMultiSetExport(numberOfCreativeSets)) {
    return `${numberOfCreativeSets} creative sets`;
  }
  return creativeSetName;
}
